/**
 * Memory Monitor Utility
 * 
 * This utility helps monitor memory usage and detect potential memory leaks
 * in the application. It should only be used in development mode.
 */

interface MemoryInfo {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

class MemoryMonitor {
  private isMonitoring = false;
  private intervalId: NodeJS.Timeout | null = null;
  private memoryHistory: number[] = [];
  private maxHistorySize = 100;
  private warningThreshold = 50 * 1024 * 1024; // 50MB
  private criticalThreshold = 100 * 1024 * 1024; // 100MB

  /**
   * Start monitoring memory usage
   */
  startMonitoring(intervalMs: number = 10000): void {
    if (this.isMonitoring) {
      return;
    }

    this.isMonitoring = true;
    this.intervalId = setInterval(() => {
      this.checkMemoryUsage();
    }, intervalMs);

    // console.log('🔍 Memory monitoring started');
  }

  /**
   * Stop monitoring memory usage
   */
  stopMonitoring(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isMonitoring = false;
    // console.log('🔍 Memory monitoring stopped');
  }

  /**
   * Check current memory usage
   */
  private checkMemoryUsage(): void {
    if (!('memory' in performance)) {
      return; // Memory API not available
    }

    const memory = (performance as any).memory as MemoryInfo;
    const usedMemory = memory.usedJSHeapSize;
    
    // Add to history
    this.memoryHistory.push(usedMemory);
    if (this.memoryHistory.length > this.maxHistorySize) {
      this.memoryHistory.shift();
    }

    // Check for memory leaks
    this.detectMemoryLeak(usedMemory);
    
    // Log memory usage
    this.logMemoryUsage(usedMemory, memory);
  }

  /**
   * Detect potential memory leaks
   */
  private detectMemoryLeak(currentMemory: number): void {
    if (this.memoryHistory.length < 10) {
      return; // Not enough data
    }

    // Check if memory is consistently growing
    const recentMemory = this.memoryHistory.slice(-10);
    const isGrowing = recentMemory.every((mem, index) => 
      index === 0 || mem >= recentMemory[index - 1]
    );

    if (isGrowing && currentMemory > this.warningThreshold) {
      console.warn('⚠️ Potential memory leak detected - memory is consistently growing');
      
      if (currentMemory > this.criticalThreshold) {
        console.error('🚨 Critical memory usage detected - consider refreshing the page');
      }
    }
  }

  /**
   * Log memory usage information
   */
  private logMemoryUsage(usedMemory: number, memory: MemoryInfo): void {
    const usedMB = (usedMemory / 1024 / 1024).toFixed(2);
    const totalMB = (memory.totalJSHeapSize / 1024 / 1024).toFixed(2);
    const limitMB = (memory.jsHeapSizeLimit / 1024 / 1024).toFixed(2);
    
    // console.log(`💾 Memory: ${usedMB}MB / ${totalMB}MB (limit: ${limitMB}MB)`);
  }

  /**
   * Get current memory usage
   */
  getCurrentMemoryUsage(): number | null {
    if (!('memory' in performance)) {
      return null;
    }

    const memory = (performance as any).memory as MemoryInfo;
    return memory.usedJSHeapSize;
  }

  /**
   * Get memory usage history
   */
  getMemoryHistory(): number[] {
    return [...this.memoryHistory];
  }

  /**
   * Clear memory history
   */
  clearHistory(): void {
    this.memoryHistory = [];
  }

  /**
   * Force garbage collection (if available)
   */
  forceGarbageCollection(): void {
    if ('gc' in window && typeof (window as any).gc === 'function') {
      (window as any).gc();
      // console.log('🗑️ Forced garbage collection');
    } else {
      // console.log('🗑️ Garbage collection not available (run with --expose-gc flag)');
    }
  }
}

// Export singleton instance
export const memoryMonitor = new MemoryMonitor();

// Auto-start monitoring in development mode
if (process.env.NODE_ENV === 'development') {
  memoryMonitor.startMonitoring(15000); // Check every 15 seconds
}

export default memoryMonitor;
